/// <amd-module name="Artifacts/FeedSettings/components/ViewsTab" />

import { AddEditViewPanel } from "Artifacts/FeedSettings/components/AddEditViewPanel";
import { setFocusOnColumnMore } from "Artifacts/FeedSettings/helpers/FeedSettingsHelpers";
import { getAccessPermissionsString } from "Artifacts/FeedSettings/helpers/ViewHelpers";
import { ViewPermissionMap } from "Artifacts/FeedSettings/redux";
import * as Res from "Artifacts/FeedSettings/Resources";
import { IFeedSettings } from "Artifacts/FeedSettings/types";
import { DefaultLoader } from "Artifacts/Root/components/DefaultLoader";
import { ComponentState } from "Artifacts/Root/Constants";
import { getCurrentCollectionName } from "Artifacts/Root/helpers/CollectionHelpers";
import { resizeColumn } from "Artifacts/Root/helpers/TableHelper";
import { useFeed } from "Artifacts/Root/hooks/FeedUrlResolver";
import { IFeed } from "Artifacts/Root/types/IFeed";
import { FeedPermission, FeedView } from "Artifacts/Root/web-apis/contracts/Feed";
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { useComponentContext } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { Icon } from "VSSUI/Icon";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { ColumnMore, ITableColumn, renderSimpleCell, SimpleTableCell, Table } from "VSSUI/Table";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

export interface IViewsTabProps {
  componentState: ComponentState;
  settings?: IFeedSettings;
  views?: FeedView[];
  viewPermissionComponentState: ComponentState;
  viewsPermissionMap?: ViewPermissionMap;
  setShowAddUpdateView: (value: boolean, setFocus?: () => void) => void;
  showAddUpdateView: boolean;
  setViewToEdit: (value: FeedView | undefined) => void;
  viewToEdit: FeedView | undefined;
  updateViewPermissions: (
    view: FeedView,
    isEdit: boolean,
    feedPermsToSet: FeedPermission[],
    isDefaultEnabled: boolean,
  ) => void;
  updateViewPermissionsComponentState: ComponentState;
  makeViewDefault: (feed: IFeed, view: FeedView) => void;
  deleteView: (viewToDelete: FeedView, setFocus: () => void) => void;
  isPublicFeed: boolean;
  isUserAdmin: boolean;
  supportServicePrincipals: boolean;
}

interface IViewTableItem {
  view: string;
  permissions: string;
  isDefault: boolean;
  feedView: FeedView;
}

const tableRef = React.createRef<Table<any>>();
let rowIndexToSetFocusOn = -1;

export const ViewsTab: React.FC<IViewsTabProps> = ({
  settings,
  views,
  componentState,
  viewsPermissionMap,
  viewPermissionComponentState,
  setShowAddUpdateView: setShowAddView,
  showAddUpdateView: showAddView,
  setViewToEdit,
  viewToEdit,
  updateViewPermissions: saveView,
  updateViewPermissionsComponentState: saveViewComponentState,
  makeViewDefault,
  deleteView,
  isPublicFeed,
  isUserAdmin,
  supportServicePrincipals
}) => {
  const { pageContext } = useComponentContext();
  const { feed } = useFeed();

  const viewTableItemProvider =
    viewsPermissionMap &&
    new ArrayItemProvider(
      views?.map<IViewTableItem>((view) => ({
        view: view.name,
        permissions: getAccessPermissionsString(
          view,
          getCurrentCollectionName(pageContext),
          viewsPermissionMap[view.id],
        ),
        isDefault: settings?.defaultViewId === view.id,
        feedView: view,
      })) || [],
    );

  function getTableColumns(): ITableColumn<IViewTableItem>[] {
    let tableColumns = [
      {
        id: "view",
        name: Res.FeedSettings_Views_Grid_View,
        readonly: true,
        renderCell: renderSimpleCell,
        width: new ObservableValue(-100),
        onSize: (event, index, width) => {
          tableColumns = resizeColumn(tableColumns, index, width);
        },
      } as ITableColumn<IViewTableItem>,
      {
        id: "permissions",
        name: Res.FeedSettings_Views_Grid_Permissions,
        readonly: true,
        renderCell: renderSimpleCell,
        width: new ObservableValue(-70),
        onSize: (event, index, width) => {
          tableColumns = resizeColumn(tableColumns, index, width);
        },
      } as ITableColumn<IViewTableItem>,
      {
        id: "isDefault",
        name: Res.FeedSettings_Views_Grid_DefaultView,
        readonly: true,
        renderCell: renderDefault,
        width: new ObservableValue(-70),
        onSize: (event, index, width) => {
          tableColumns = resizeColumn(tableColumns, index, width);
        },
      } as ITableColumn<IViewTableItem>,
      new ColumnMore<IViewTableItem>((item, rowIndex, buttonRef) => ({
        id: "sub-menu",
        items: [
          {
            id: "delete",
            onActivate: () => {
              rowIndexToSetFocusOn = rowIndex;
              feed &&
                deleteView(item.feedView, () =>
                  setFocusOnColumnMore(tableRef, rowIndexToSetFocusOn),
                );
            },
            text: Res.Delete_Label,
          },
          {
            id: "edit",
            onActivate: () => {
              rowIndexToSetFocusOn = rowIndex;
              setViewToEdit(item.feedView);
              setShowAddView(true);
            },
            text: Res.FeedSettings_Views_EditView_Label,
          },
          {
            id: "makeDefault",
            onActivate: () => {
              feed && makeViewDefault(feed, item.feedView);
              buttonRef?.current?.focus();
            },
            disabled: item.isDefault,
            text: Res.FeedSettings_Views_SetDefaultViewButton_Label,
          },
        ],
      })),
    ];

    // Remove permissions column on public feeds
    // because everyone can access all views on public feeds.
    if (isPublicFeed) {
      tableColumns = tableColumns.filter((col) => col.id !== "permissions");
    }

    return tableColumns;
  }

  return (
    <DefaultLoader state={componentState}>
      <DefaultLoader state={viewPermissionComponentState}>
        {isPublicFeed && (
          <div className="public-feed-message">
            <MessageCard severity={MessageCardSeverity.Info}>
              {Res.FeedSettings_Views_PublicFeedPermissionMessage}
            </MessageCard>
          </div>
        )}
        <Card
          className="flex-grow bolt-table-card"
          contentProps={{ contentPadding: false }}
          titleProps={{ text: Res.FeedSettings_Views_Title, ariaLevel: 2 }}
          headerCommandBarItems={[
            {
              id: "add",
              text: Res.FeedSettings_Views_AddView_Label,
              isPrimary: true,
              onActivate: () => setShowAddView(true),
              disabled: !isUserAdmin,
            },
          ]}
        >
          <Table columns={getTableColumns()} itemProvider={viewTableItemProvider} ref={tableRef} />
        </Card>
        {feed && settings && showAddView && viewsPermissionMap && (
          <AddEditViewPanel
            onDismiss={() => {
              setViewToEdit(undefined);
              setShowAddView(false, () => setFocusOnColumnMore(tableRef, rowIndexToSetFocusOn));
            }}
            viewToEdit={viewToEdit}
            feed={feed}
            settings={settings}
            supportServicePrincipals={supportServicePrincipals}
            onSave={(view, feedPermsToSet, isDefaultEnabled) =>
              saveView(view, viewToEdit != null, feedPermsToSet, isDefaultEnabled)
            }
            saveButtonComponentState={saveViewComponentState}
            currentViewPermissions={(viewToEdit && viewsPermissionMap[viewToEdit.id]) || []}
          />
        )}
      </DefaultLoader>
    </DefaultLoader>
  );
};

function renderDefault(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IViewTableItem>,
  tableItem: IViewTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      {tableItem.isDefault && <Icon ariaLabel="Check mark" iconName="CheckMark" />}
    </SimpleTableCell>
  );
}
